import { Box, Grid, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React, { useState } from "react";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import { copyJsonObject } from "../../util/commonUtils";
import { getThanksGivingDateOfTheYear } from "../../util/dateHelper";
import { convertIsoDateToLuxon } from "../../util/dateTimeFormatter";
import { validateAllotmentValue } from "../../util/bid/bidInputValidation";
import { isHoliday } from "../../util/bid/timeOffBidUtils";

const useStyles = makeStyles((theme) => ({
  inputText: {
    marginTop: "20px",
    marginLeft: "10px",
  },
}));

const AllotmentsOverrideDialog = (props) => {
  const classes = useStyles();
  const [allotmentValue, setAllotmentValue] = useState("");
  const [allotmentValueErrors, setAllotmentValueErrors] = useState("");

  const updateAllotmentDayState = (value) => {
    const copiedAllotmentDayState = copyJsonObject(props.allotmentDayState);
    const updatedAllotmentDayState = copiedAllotmentDayState.map((dayState) => {
      // do not update placeholder dates
      if (!dayState?.date) {
        return dayState;
      }

      // do not update thanksgiving and christmas dates
      const dateObject = convertIsoDateToLuxon(dayState.date);
      const converted_year = parseInt(props.schedYear);
      const thanksgivingDate = getThanksGivingDateOfTheYear(converted_year);

      if (isHoliday(dateObject, converted_year, thanksgivingDate)) {
        return dayState;
      }

      // update remaining dates with overridden value
      return {
        date: dayState.date,
        amOrPmCount: parseInt(value),
      };
    });

    props.setAllotmentDayState(updatedAllotmentDayState);
  };

  const handleAllotmentValueChange = (e) => {
    const allotmentValue = e.target.value.trim().toLowerCase();
    const validationErrors = validateAllotmentValue(allotmentValue, props.calcDailyAllotments?.amOrPmCount);
    setAllotmentValueErrors(validationErrors);
    setAllotmentValue(allotmentValue);
  };

  const handleOverrideClick = () => {
    const validationErrors = validateAllotmentValue(allotmentValue, props.calcDailyAllotments?.amOrPmCount);
    setAllotmentValueErrors(validationErrors);

    if (!validationErrors) {
      updateAllotmentDayState(allotmentValue);
      setAllotmentValue("");
      setAllotmentValueErrors("");
      props.onClickDialogClose();
    }
  };

  const handleCancelClicked = () => {
    setAllotmentValue("");
    setAllotmentValueErrors("");
    props.onClickDialogClose();
  };

  return (
    <Dialog
      open={props.isDialogOpen}
      TransitionComponent={SlideUpTransition}
      keepMounted
      onClose={handleCancelClicked}
      maxWidth={"sm"}
    >
      <Box p={1}>
        <DialogTitle>Override Allotments</DialogTitle>
        <DialogContent sx={{ margin: "0 45px" }}>
          <Grid container sx={{ alignItems: "center" }}>
            <Grid item xs={6}>
              <Typography fontSize={"14px"}>Enter the # of daily allotments to apply to all the days:</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                className={classes.inputText}
                label="# of allotments"
                variant="outlined"
                value={allotmentValue}
                onChange={handleAllotmentValueChange}
                error={allotmentValueErrors !== ""}
                helperText={allotmentValueErrors}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            id="cancelButton"
            sx={{ width: "100px" }}
            variant="outlined"
            color="primary"
            onClick={handleCancelClicked}
            size="large"
          >
            Cancel
          </Button>
          <Button
            id="overrideButton"
            sx={{ width: "100px" }}
            variant="contained"
            color="primary"
            onClick={handleOverrideClick}
            disabled={allotmentValueErrors}
            size="large"
          >
            Override
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

AllotmentsOverrideDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
  allotmentDayState: PropTypes.object.isRequired,
  setAllotmentDayState: PropTypes.object.isRequired,
  schedMonth: PropTypes.string.isRequired,
  schedYear: PropTypes.string.isRequired,
  calcDailyAllotments: PropTypes.number,
};

export default AllotmentsOverrideDialog;
